import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../../context/AppStateContext';
import {
    DocumentCheckIcon,
    MagnifyingGlassIcon,
    AcademicCapIcon,
    ClockIcon,
    DocumentTextIcon,
    PencilSquareIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import GradeForm from '../../components/GradeForm';
import { useApi } from '../../hooks/useApi';
import { useQueryClient } from '@tanstack/react-query';
import AssignmentCreator from '../../components/teacher/AssignmentCreator';
import DeleteAssignmentModal from '../../components/teacher/DeleteAssignmentModal';

const GlobalSubmissionManager = () => {
    const { courses, showToast, refresh } = useAppState();
    const { del } = useApi();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
    const [showCreator, setShowCreator] = useState(false);
    const [editingAssignment, setEditingAssignment] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, assignment: null, loading: false });

    // Flatten assignments from all courses
    const allAssignments = useMemo(() => {
        return courses.flatMap(course =>
            (course.assignments || []).map(asgn => ({
                ...asgn,
                courseId: course.id,
                courseName: course.name,
                courseCode: course.code,
                gradeLevel: course.grade_level_name || course.grade_level || 'Grade',
                stream: course.stream_display_name || '',
                // If manual, pending work is for all students who haven't been graded yet
                // If digital, pending work is for tasks submitted but not yet graded
                ungradedCount: asgn.is_manual 
                    ? Math.max(0, (asgn.enrolled_count || 0) - (asgn.graded_submissions_count || 0))
                    : Math.max(0, (asgn.submission_count || 0) - (asgn.graded_submissions_count || 0))
            }))
        ).sort((a, b) => {
            if (b.ungradedCount !== a.ungradedCount) {
                return b.ungradedCount - a.ungradedCount;
            }
            return new Date(b.due_date) - new Date(a.due_date);
        });
    }, [courses]);

    const filteredAssignments = useMemo(() => {
        if (!searchTerm) return allAssignments;
        const lowerTerm = searchTerm.toLowerCase();
        return allAssignments.filter(a =>
            a.title.toLowerCase().includes(lowerTerm) ||
            a.courseName.toLowerCase().includes(lowerTerm) ||
            a.courseCode.toLowerCase().includes(lowerTerm)
        );
    }, [allAssignments, searchTerm]);

    const handleEditAssignment = (assignment) => {
        setEditingAssignment(assignment);
        setShowCreator(true);
    };

    const handleDeleteAssignment = (assignment) => {
        setDeleteModal({ isOpen: true, assignment, loading: false });
    };

    const confirmDelete = async () => {
        const { assignment } = deleteModal;
        try {
            setDeleteModal(prev => ({ ...prev, loading: true }));
            await del(`/api/assignments/${assignment.id}/`);
            showToast('Assessment deleted successfully');
            queryClient.invalidateQueries({ queryKey: ['assignments'] });
            refresh(); // Sync global app state
            setDeleteModal({ isOpen: false, assignment: null, loading: false });
        } catch (error) {
            console.error('Error deleting assignment:', error);
            showToast('Failed to delete assessment', 'error');
            setDeleteModal(prev => ({ ...prev, loading: false }));
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 pb-20">
            <main className="max-w-7xl mx-auto py-6 md:py-10 px-4 sm:px-6 lg:px-8 space-y-6 md:space-y-10">
                {/* Desktop Header */}
                <header className="hidden md:block relative overflow-hidden bg-[#18216D] rounded-[2.5rem] p-12 text-white shadow-2xl shadow-indigo-900/20">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFC425]/10 rounded-full -mr-32 -mt-32 blur-3xl"></div>
                    <div className="relative z-10">
                        <p className="text-[10px] font-black uppercase tracking-[0.4em] text-[#FFC425] mb-2">Grading Queue</p>
                        <h1 className="text-4xl sm:text-6xl font-black tracking-tighter italic">Unified Submissions</h1>
                        <p className="text-indigo-100/70 max-w-2xl font-medium text-lg mt-6">
                            Review and grade work across all your assigned learning areas from a single view.
                        </p>
                    </div>
                </header>

                {/* Mobile Header */}
                <div className="md:hidden">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="h-1 w-8 bg-[#FFC425] rounded-full"></div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Grading Queue</p>
                    </div>
                    <h1 className="text-4xl font-black text-[#18216D] tracking-tighter mb-6">Unified Submissions</h1>

                    <div className="flex items-center gap-3 mb-2">
                        <div className="flex-1 bg-white p-3 rounded-2xl border border-slate-100 flex items-center justify-between shadow-sm">
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Total</div>
                            <div className="text-lg font-black text-[#18216D]">{filteredAssignments.length}</div>
                        </div>
                        <div className="flex-1 bg-[#18216D] p-3 rounded-2xl border border-[#18216D] flex items-center justify-between shadow-lg shadow-indigo-900/20">
                            <div className="text-[10px] font-black uppercase tracking-widest text-[#FFC425]">Pending</div>
                            <div className="text-lg font-black text-white">{filteredAssignments.reduce((acc, a) => acc + a.ungradedCount, 0)}</div>
                        </div>
                    </div>
                </div>

                <section className="md:bg-white md:rounded-3xl md:p-6 md:shadow-sm md:border md:border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex-1 relative group bg-transparent md:bg-white">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-[#18216D] transition-colors z-10">
                            <MagnifyingGlassIcon className="w-5 h-5" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search assignments..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-6 py-4 bg-white md:bg-slate-50 border-none rounded-2xl shadow-sm md:shadow-none focus:ring-2 focus:ring-[#18216D]/20 transition-all text-sm font-bold text-[#18216D] placeholder:text-slate-400"
                        />
                    </div>
                </section>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                    {filteredAssignments.map(assignment => {
                        return (
                            <div key={assignment.id} className="bg-white p-8 rounded-[2.5rem] border border-slate-100 hover:border-indigo-100 hover:shadow-2xl hover:shadow-indigo-900/5 transition-all group animate-in fade-in duration-500">
                                {/* Assignment Info Header */}
                                <div className="flex items-start gap-4 mb-6">
                                    {/* Use enrolled_count if available, fallback to submission_count if not */}
                                    {(() => {
                                        const enrolled = assignment.enrolled_count || 0;
                                        const graded = assignment.graded_submissions_count || 0;
                                        const total = assignment.submission_count || 0;
                                        const isGraded = enrolled > 0 ? (graded >= enrolled) : (total > 0 && graded >= total);
                                        
                                        return (
                                            <div className={`h-14 w-14 rounded-2xl flex items-center justify-center shadow-sm border border-white transition-transform group-hover:scale-110 ${!isGraded ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
                                                {!isGraded ? <ClockIcon className="w-7 h-7" /> : <AcademicCapIcon className="w-7 h-7" />}
                                            </div>
                                        );
                                    })()}
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-3">
                                                <span className="px-2.5 py-1 rounded-lg bg-[#18216D]/5 text-[#18216D] text-[10px] font-black uppercase tracking-widest border border-[#18216D]/10">
                                                    {assignment.courseName}
                                                </span>
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                    {assignment.gradeLevel} {assignment.stream && `(${assignment.stream})`}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleEditAssignment(assignment)}
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    title="Edit Assessment"
                                                >
                                                    <PencilSquareIcon className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteAssignment(assignment)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Delete Assessment"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                        <h3 className="text-xl font-black text-[#18216D] tracking-tight">{assignment.title}</h3>
                                        <p className="text-slate-400 text-[10px] font-bold uppercase tracking-widest mt-1">Due {new Date(assignment.due_date).toLocaleDateString([], { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                                    </div>
                                </div>

                                {/* Action Navigation Row */}
                                <div className="flex flex-wrap items-center gap-3 mb-8 py-4 border-y border-slate-50">
                                    {assignment.is_manual ? (
                                        <button
                                            onClick={() => navigate(`/teacher/courses/${assignment.courseId}/grade/${assignment.id}`)}
                                            className="h-10 px-6 bg-[#18216D] text-white rounded-xl font-black text-[9px] uppercase tracking-widest hover:bg-[#0D164F] transition-all flex items-center gap-2"
                                        >
                                            <AcademicCapIcon className="w-3.5 h-3.5" />
                                            Grade Tasks
                                        </button>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => navigate(`/teacher/assignments/${assignment.id}/submissions`)}
                                                className="h-10 px-6 bg-white text-[#18216D] rounded-xl border border-slate-200 hover:border-[#18216D] transition-all flex items-center gap-2"
                                                title="View Submissions"
                                            >
                                                <DocumentTextIcon className="w-3.5 h-3.5" />
                                                <span className="text-[9px] font-black uppercase tracking-widest">Submissions</span>
                                            </button>
                                            <button
                                                onClick={() => {
                                                    if (assignment.first_pending_submission_id) {
                                                        navigate(`/teacher/assignments/${assignment.id}/mark/${assignment.first_pending_submission_id}`);
                                                    } else {
                                                        navigate(`/teacher/assignments/${assignment.id}/submissions`);
                                                    }
                                                }}
                                                className={`h-10 px-6 rounded-xl font-black text-[9px] uppercase tracking-widest transition-all flex items-center gap-2 ${assignment.first_pending_submission_id ? 'bg-[#18216D] text-white hover:bg-[#0D164F]' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                                                title={assignment.first_pending_submission_id ? "Smart marking: Jump to first pending student" : "No submissions available to mark"}
                                            >
                                                <PencilSquareIcon className="w-3.5 h-3.5" />
                                                <span className="text-[9px] font-black uppercase tracking-widest">Mark</span>
                                            </button>
                                        </>
                                    )}
                                </div>

                                 {/* Completion Statistics */}
                                {(() => {
                                    const enrolled = assignment.enrolled_count || 0;
                                    const graded = assignment.graded_submissions_count || 0;
                                    const total = assignment.submission_count || 0;
                                    
                                    // Percentage of total ENROLLED students who have been graded
                                    const completionPercentage = enrolled > 0 ? Math.round((graded / enrolled) * 100) : (total > 0 ? Math.round((graded / total) * 100) : 0);
                                    
                                    return (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-widest">
                                                <span className="text-slate-400">Class Completion</span>
                                                <span className="text-[#18216D]">
                                                    {graded} / {enrolled || total} {enrolled > 0 ? 'Students' : 'Submissions'} Graded
                                                </span>
                                            </div>
                                            <div className="h-4 w-full bg-slate-50 rounded-full overflow-hidden border border-slate-100">
                                                <div
                                                    className={`h-full rounded-full transition-all duration-1000 ${completionPercentage >= 100 ? 'bg-emerald-500' : 'bg-[#FFC425] shadow-[0_0_20px_rgba(255,196,37,0.3)]'}`}
                                                    style={{ width: `${Math.min(completionPercentage, 100)}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        );
                    })}

                    {filteredAssignments.length === 0 && (
                        <div className="text-center py-24 bg-white rounded-[2.5rem] border-2 border-dashed border-slate-100">
                            <DocumentCheckIcon className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                            <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No assignments match your search.</p>
                        </div>
                    )}
                </div>
            </main>

            {selectedAssignmentId && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-[3rem] shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto overflow-x-hidden animate-in zoom-in duration-300">
                        <GradeForm assignmentId={selectedAssignmentId} onClose={() => setSelectedAssignmentId(null)} />
                    </div>
                </div>
            )}

            {/* Assessment Creator Modal */}
            {showCreator && (
                <AssignmentCreator
                    courseId={editingAssignment?.courseId}
                    assignment={editingAssignment}
                    onClose={() => {
                        setShowCreator(false);
                        setEditingAssignment(null);
                    }}
                    onSave={() => {
                        queryClient.invalidateQueries({ queryKey: ['assignments'] });
                        refresh();
                    }}
                />
            )}

            {/* Deletion Modal */}
            <DeleteAssignmentModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, assignment: null, loading: false })}
                onConfirm={confirmDelete}
                title={deleteModal.assignment?.title}
                loading={deleteModal.loading}
            />
        </div>
    );
};

export default GlobalSubmissionManager;
